import { identity } from '@/api/client';
import type {
  CredentialType,
  JourneyTimelineEvent,
  PublicCredential,
  RecentVerification,
  VerificationCredential,
  VerificationOutcome,
  VerificationPurpose,
} from '@/types/verification';

/**
 * Institutional identity verification — credentials, scans and journey
 * timelines — on campusone-identity-service (src/verification/*), reached
 * through the identity BFF.
 *
 * Boundary-validated like src/api/studio.ts: every response is normalised by a
 * hand-rolled guard before it reaches React state, so a contract drift fails
 * loudly at the boundary ("the server returned X this app cannot read") instead
 * of as an undefined deep in a component. List normalisers DROP unreadable rows
 * rather than failing the whole list — one bad row must not blank a console.
 */

const BASE = '/verification';
const seg = encodeURIComponent;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function fail(what: string): never {
  throw new Error(`The server returned ${what} this app cannot read.`);
}

/** Dates arrive as ISO strings through JSON; anything else becomes null. */
function isoOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function credential(payload: unknown): VerificationCredential {
  if (
    !isRecord(payload) ||
    typeof payload.id !== 'string' ||
    typeof payload.credentialPublicId !== 'string' ||
    typeof payload.credentialType !== 'string' ||
    typeof payload.status !== 'string'
  ) {
    fail('a credential');
  }
  return {
    id: payload.id,
    credentialPublicId: payload.credentialPublicId,
    credentialType: payload.credentialType,
    status: payload.status,
    issuedAt: isoOrNull(payload.issuedAt) ?? '',
    expiresAt: isoOrNull(payload.expiresAt),
    // The list projection omits personId; lifecycle actions return the entity
    // which carries it. Null keeps the two shapes under one type.
    personId: typeof payload.personId === 'string' ? payload.personId : null,
    metadata: isRecord(payload.metadata) ? payload.metadata : {},
  };
}

function credentialList(payload: unknown): VerificationCredential[] {
  if (!Array.isArray(payload)) fail('a credential list');
  return payload.flatMap((row) => {
    try {
      return [credential(row)];
    } catch {
      return [];
    }
  });
}

function outcome(payload: unknown): VerificationOutcome {
  if (!isRecord(payload) || (payload.result !== 'SUCCESS' && payload.result !== 'FAILURE')) {
    fail('a verification outcome');
  }
  const person = isRecord(payload.person) ? payload.person : null;
  const journeyEvent = isRecord(payload.journeyEvent) ? payload.journeyEvent : null;
  const attendance = isRecord(payload.attendance) ? payload.attendance : null;
  return {
    result: payload.result,
    ...(typeof payload.failureCode === 'string' ? { failureCode: payload.failureCode } : {}),
    ...(typeof payload.message === 'string' ? { message: payload.message } : {}),
    ...(typeof payload.verificationId === 'string' ? { verificationId: payload.verificationId } : {}),
    ...(isoOrNull(payload.occurredAt) ? { occurredAt: isoOrNull(payload.occurredAt)! } : {}),
    ...(typeof payload.purpose === 'string' ? { purpose: payload.purpose } : {}),
    ...(typeof payload.duplicate === 'boolean' ? { duplicate: payload.duplicate } : {}),
    ...(person &&
    typeof person.personId === 'string' &&
    typeof person.displayName === 'string'
      ? {
          person: {
            personId: person.personId,
            firstName: typeof person.firstName === 'string' ? person.firstName : '',
            lastName: typeof person.lastName === 'string' ? person.lastName : '',
            displayName: person.displayName,
            ...(typeof person.studentReference === 'string'
              ? { studentReference: person.studentReference }
              : {}),
          },
        }
      : {}),
    ...(journeyEvent &&
    typeof journeyEvent.eventId === 'string' &&
    typeof journeyEvent.eventType === 'string'
      ? {
          journeyEvent: {
            eventId: journeyEvent.eventId,
            eventType: journeyEvent.eventType,
            occurredAt: isoOrNull(journeyEvent.occurredAt) ?? '',
          },
        }
      : {}),
    ...(attendance && typeof attendance.recorded === 'boolean'
      ? {
          attendance: {
            recorded: attendance.recorded,
            ...(typeof attendance.attendanceId === 'string'
              ? { attendanceId: attendance.attendanceId }
              : {}),
          },
        }
      : {}),
  };
}

function recentVerification(payload: unknown): RecentVerification {
  if (
    !isRecord(payload) ||
    typeof payload.id !== 'string' ||
    typeof payload.purpose !== 'string' ||
    typeof payload.result !== 'string'
  ) {
    fail('a verification record');
  }
  return {
    id: payload.id,
    occurredAt: isoOrNull(payload.occurredAt) ?? '',
    purpose: payload.purpose,
    result: payload.result,
    failureCode: typeof payload.failureCode === 'string' ? payload.failureCode : null,
    personId: typeof payload.personId === 'string' ? payload.personId : null,
    verifiedBy: typeof payload.verifiedBy === 'string' ? payload.verifiedBy : null,
    context: isRecord(payload.context) ? payload.context : {},
  };
}

function recentList(payload: unknown): RecentVerification[] {
  if (!Array.isArray(payload)) fail('a verification history');
  return payload.flatMap((row) => {
    try {
      return [recentVerification(row)];
    } catch {
      return [];
    }
  });
}

function timelineEvent(payload: unknown): JourneyTimelineEvent {
  if (
    !isRecord(payload) ||
    typeof payload.eventId !== 'string' ||
    typeof payload.eventType !== 'string'
  ) {
    fail('a journey event');
  }
  return {
    eventId: payload.eventId,
    eventType: payload.eventType,
    occurredAt: isoOrNull(payload.occurredAt) ?? '',
    context: isRecord(payload.context) ? payload.context : {},
    externalEventId: typeof payload.externalEventId === 'string' ? payload.externalEventId : null,
  };
}

function timelineList(payload: unknown): JourneyTimelineEvent[] {
  if (!Array.isArray(payload)) fail('a journey timeline');
  return payload.flatMap((row) => {
    try {
      return [timelineEvent(row)];
    } catch {
      return [];
    }
  });
}

function publicCredential(payload: unknown): PublicCredential {
  if (
    !isRecord(payload) ||
    typeof payload.credentialPublicId !== 'string' ||
    typeof payload.status !== 'string' ||
    typeof payload.credentialType !== 'string' ||
    !isRecord(payload.person) ||
    typeof payload.person.displayName !== 'string'
  ) {
    fail('a credential preview');
  }
  return {
    credentialPublicId: payload.credentialPublicId,
    status: payload.status,
    credentialType: payload.credentialType,
    person: {
      displayName: payload.person.displayName,
      photographUrl:
        typeof payload.person.photographUrl === 'string' ? payload.person.photographUrl : null,
      personStatus:
        typeof payload.person.personStatus === 'string' ? payload.person.personStatus : '',
    },
    expiresAt: isoOrNull(payload.expiresAt),
  };
}

const post = (payload: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(payload) });

export const verificationApi = {
  credentials: {
    /** Requires `credential.issue`. The person and institution are identity-service UUIDs. */
    issue: (body: {
      personId: string;
      institutionId: string;
      credentialType: CredentialType;
      expiresAt?: string;
      metadata?: Record<string, unknown>;
    }) => identity<unknown>(`${BASE}/credentials`, post(body)).then(credential),

    /**
     * Lists the CALLER'S OWN credentials — the deployed controller resolves the
     * actor and calls listForPerson(scope.personId); it accepts no personId or
     * status query. The console labels this panel accordingly instead of
     * pretending it can browse another person's cards. (credential.read)
     */
    list: () => identity<unknown>(`${BASE}/credentials`).then(credentialList),

    get: (id: string) => identity<unknown>(`${BASE}/credentials/${seg(id)}`).then(credential),

    suspend: (id: string, reason: string) =>
      identity<unknown>(`${BASE}/credentials/${seg(id)}/suspend`, post({ reason })).then(credential),

    reactivate: (id: string) =>
      identity<unknown>(`${BASE}/credentials/${seg(id)}/reactivate`, post({})).then(credential),

    revoke: (id: string, reason: string) =>
      identity<unknown>(`${BASE}/credentials/${seg(id)}/revoke`, post({ reason })).then(credential),

    /** Revokes the old card and issues its successor in one server transaction. */
    replace: (id: string, body: { credentialType?: CredentialType; expiresAt?: string }) =>
      identity<unknown>(`${BASE}/credentials/${seg(id)}/replace`, post(body)).then(credential),

    /**
     * The unauthenticated-preview read the scan console shows BEFORE recording
     * anything. Being added in a parallel backend lane — a 404 here means "not
     * deployed yet" (ApiError.isMissingEndpoint) and the page says so honestly.
     */
    getPublic: (credentialPublicId: string) =>
      identity<unknown>(`${BASE}/credentials/public/${seg(credentialPublicId)}`).then(
        publicCredential,
      ),
  },

  /**
   * Records one staff-performed scan. The server checks the purpose against the
   * caller's verification.<purpose> permission and answers HTTP 200 for success
   * AND refusal — a refusal is a typed outcome, not an exception.
   */
  verify: (body: {
    rawQrContent: string;
    purpose: VerificationPurpose;
    context?: Record<string, unknown>;
  }) => identity<unknown>(`${BASE}/verifications`, post(body)).then(outcome),

  /** Recent scans of the caller's institution, newest first. (verification.identity) */
  recent: () => identity<unknown>(`${BASE}/verifications/recent`).then(recentList),

  /**
   * A person's journey timeline. Server-enforced scope: your own, or a student
   * linked to you by a verified academic-access guardian relationship.
   *
   * No eventType query is sent even though the DTO declares one: the DTO
   * validates it against VerificationPurpose while the data stores
   * JourneyEventType, so ARRIVAL is rejected with 400 and SCHOOL_ARRIVAL
   * matches nothing. Filtering happens client-side until that is fixed.
   */
  timeline: (personId: string, query?: { from?: string; to?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (query?.from) params.set('from', query.from);
    if (query?.to) params.set('to', query.to);
    if (query?.limit) params.set('limit', String(query.limit));
    const qs = params.toString();
    return identity<unknown>(
      `${BASE}/persons/${seg(personId)}/timeline${qs ? `?${qs}` : ''}`,
    ).then(timelineList);
  },

  /**
   * The caller's own identity-service personId, needed for the self view of the
   * journey page. /me answers `personId` (the CampusOne human) alongside the
   * Keycloak `subject`; null when the account is not linked to a person yet.
   */
  myPersonId: () =>
    identity<unknown>('/me').then((payload) =>
      isRecord(payload) && typeof payload.personId === 'string' ? payload.personId : null,
    ),
};
